import { DictType } from '@pma/types';
import { Card, CardContent, CardHeader } from '@components/common/card';
import { Separator } from '@components/common/separator';
import { PROJECTS_PAGE_ROUTE } from '@pma/utils/page-routes';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@components/common/button';
import DeleteTask from './delete-task';
import TaskEditor from './task-editor';
import TaskStatusEditor from './task-status-editor';
import AssignToUser from './assign-to-user';
import useTranslator from '@hooks/use-translator';
import ExpandTask from './expand-task';
import {
  formatDate,
  formatDate_he,
  formatStringWithLink,
} from '@pma/utils/helpers';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@components/common/tooltip';
import { Eye } from 'lucide-react';
import { useAuth } from '@stores/auth-store';
import PinTask from './pin-task';

interface Props {
  task: DictType;
}

const TaskCard = ({ task }: Props) => {
  const { setTabManager } = useAuth();
  const { translate, locale } = useTranslator();
  const isHebrew = locale === 'he';

  const navigate = useNavigate();

  const openTask = () => {
    setTabManager({ group: "project-detail", main: 'tasks', sub: 'groups' });
    navigate(
      PROJECTS_PAGE_ROUTE + `/${task?.project_id}` + `?task=${task?.id}`,
    );
  };

  const handleCardClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    // Prevent navigation for interactive elements
    if (!target.classList.contains("edit-icon")) {
      openTask()
    }

  };

  return (
    <Card onClick={handleCardClick} className="cursor-pointer hover:shadow-lg w-full">
      <CardHeader className='h-9 flex flex-row justify-between items-center w-full py-2'>
        <span className='text-xs text-slate-500'>{translate('Created')}</span>
        <span className='text-xs font-semibold'>
          {isHebrew
            ? formatDate_he(task?.created_at)
            : formatDate(task?.created_at)}
        </span>
      </CardHeader>
      <Separator />
      <CardContent className='flex flex-col gap-2 p-2 overflow-hidden'>
        <TaskEditor id={task?.id} title={'title'} value={task?.title}>
          <div
            className='text-sm pt-1 text-wrap text-ellipsis max-w-[35ch] text-left'
            dangerouslySetInnerHTML={{
              __html: formatStringWithLink(task.title),
            }}
          />
        </TaskEditor>
        <TaskEditor
          id={task?.id}
          title={'description'}
          value={task?.description}
        >
          <p className='text-xs text-slate-500 max-w-[42ch] truncate text-ellipsis'>
            {task?.description ? (
              <div
                className='w-fit text-left'
                dangerouslySetInnerHTML={{
                  __html: formatStringWithLink(task?.description),
                }}
              />
            ) : (
              '---'
            )}
          </p>
        </TaskEditor>

        <div className='flex w-full items-center justify-between' onClick={(e) => e.stopPropagation()}>
          {task?.status && (
            <div className='flex flex-1 items-center gap-2'>
              <span className='text-slate-500 text-xs truncate text-ellipsis'>
                {translate('Status')}:
              </span>
              <TaskStatusEditor status={task?.status} taskId={task?.id} />
            </div>
          )}
          {
            task?.responsible_id && (
              <AssignToUser
                taskId={task?.id}
                responsible={task?.responsible_id || []}
              />
            )
            // )
          }
        </div>

        {task?.project?.title && (
          <div className='flex w-full items-center justify-between'>
            <div className='flex flex-1 items-center gap-2'>
              <span className='text-slate-500 text-xs truncate text-ellipsis'>
                {translate('Project')}:
                <Link to={PROJECTS_PAGE_ROUTE + `/${task?.project?.id}`}>
                  <Button variant={'link'} className='text-sm py-2'>
                    {task?.project?.title}
                  </Button>
                </Link>
              </span>
            </div>
          </div>
        )}

        <div className='flex w-full items-center justify-between gap-0.5' onClick={(e) => e.stopPropagation()}>
          <small className='text-xs text-slate-500'>{task?.number}</small>{' '}
          <div className='flex flex-1 items-center justify-end'>
            {task?._project?.title && (
              <div className='flex w-full items-center justify-between flex-1'>
                <div className='flex flex-1 items-center gap-2'>
                  <span className='text-slate-500 text-xs '>
                    {translate('Project')}:
                    <Link to={PROJECTS_PAGE_ROUTE + `/${task?.project_id}`}>
                      <Button
                        className='h-6 text-sm py-2 truncate text-ellipsis p-0 mx-1'
                        variant={'link'}
                      >
                        {task?._project?.title}
                      </Button>
                    </Link>
                  </span>
                </div>
              </div>
            )}
            <PinTask task={task} />
            <ExpandTask task={task} />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={'link'}
                  className='w-6 h-6'
                  onClick={openTask}
                  size={'icon'}
                >
                  <Eye size={14} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{translate('Go to task')}</TooltipContent>
            </Tooltip>
            <DeleteTask task={task} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TaskCard;
